'use client'
import { getOversData } from '@/app/libs/match'
import { RECENT_OVER } from '@/graphql/match/match.query'
import Loading from '@/shared/components/loading'
import usePolling from '@/shared/hooks/usePolling'
import { getInningsSuffix } from '@/shared/utils'
import { useQuery } from '@apollo/client'
import { forwardRef, useEffect, useRef, useState } from 'react'

export default function OverRibbon({
  matchDetail,
  initialOvers
}: {
  matchDetail?: any
  initialOvers?: any[]
}) {
  const [overs, setOvers] = useState(initialOvers || [])
  const ref = useRef<HTMLDivElement>()
  const previousOver = useRef<{ overLength: number; over: string }>()
  const { _id, nLatestInningNumber } = matchDetail || {}

  const { loading } = useQuery(RECENT_OVER, {
    variables: {
      input: { iMatchId: _id, nLimit: 5, nSkip: 0, nInningNumber: nLatestInningNumber }
    },
    onCompleted: data => {
      const oversData = (data?.listMatchOvers?.aResults || []).toReversed()
      setOvers(oversData)
      previousOver.current = {
        over: oversData?.at(-1)?.sOver,
        overLength: oversData?.at(-1)?.aBall?.length
      }
      setTimeout(() => {
        ref.current?.scrollTo({
          left: ref.current.scrollWidth,
          behavior: 'smooth'
        })
      }, 100)
    }
  })

  useEffect(() => {
    ref.current?.scrollTo({
      left: ref.current.scrollWidth,
      behavior: 'smooth'
    })
  }, [])

  usePolling(
    () => {
      getOversData({ iMatchId: _id, nLatestInningNumber }).then(data => {
        const oversData = (data?.listMatchOvers?.aResults || []).toReversed()
        setOvers(oversData)

        if (
          previousOver.current &&
          (previousOver.current.overLength !== oversData?.at(-1)?.aBall?.length ||
            previousOver.current.over !== oversData?.at(-1)?.sOver)
        ) {
          previousOver.current = {
            over: oversData?.at(-1)?.sOver,
            overLength: oversData?.at(-1)?.aBall?.length
          }
          setTimeout(() => {
            ref.current?.scrollTo({
              left: ref.current.scrollWidth,
              behavior: 'smooth'
            })
          }, 100)
        }
      })
    },
    {
      deps: [_id, matchDetail?.oStatus?.sValue],
      interval: 10000,
      skip:
        !_id ||
        matchDetail?.oStatus?.sValue === 'completed' ||
        matchDetail?.oStatus.sValue === 'cancelled'
    }
  )
  return (
    <div className="my-4 md:mt-0">
      {loading ? <Loading /> : <Scoreboard ref={ref} overs={overs || []} />}
    </div>
  )
}

const Ball = ({ value }: any) => {
  if (value === 'w') {
    return (
      <div
        className={`px-1 text-sm min-w-6 md:min-w-8 h-6 md:h-8 flex items-center justify-center rounded-full bg-WicketBg border-WicketBg text-white`}
      >
        {value?.toUpperCase()}
      </div>
    )
  } else if (value === '4') {
    return (
      <div
        className={`px-1 text-sm min-w-6 md:min-w-8 h-6 md:h-8 flex items-center justify-center rounded-full border bg-blueScore border-blueScore text-white`}
      >
        {value}
      </div>
    )
  } else if (value === '6') {
    return (
      <div
        className={`px-1 text-sm min-w-6 md:min-w-8 h-6 md:h-8 flex items-center justify-center rounded-full border bg-blueScore border-blueScore text-white`}
      >
        {value}
      </div>
    )
  } else if (value) {
    return (
      <div
        className={`px-1 text-sm min-w-6 md:min-w-8 h-6 md:h-8 flex items-center justify-center rounded-full bg-headersActive border border-tabBorder`}
      >
        {value}
      </div>
    )
  }
  return null
}

const Scoreboard = forwardRef(function Scoreboard({ overs }: { overs: any }, ref: any) {
  return (
    <div ref={ref} className="flex items-center space-x-4 font-mono text-sm scroll-list">
      {overs?.map((over: any, index: number) => {
        const currentOver = index === overs.length - 1
        return (
          <>
            <div className="border-l border-tabBorder h-6 first:hidden"></div>
            <div key={index} className="flex items-center space-x-2">
              <span className="whitespace-nowrap">
                {currentOver ? 'This' : getInningsSuffix(Number(over.sOver))} Over:
              </span>
              {over.aBall.map((ball: { sScore: string }, index: number) => (
                <Ball key={index} value={ball.sScore} />
              ))}
              <span className="whitespace-nowrap">= {over.nOverTotal}</span>
            </div>
          </>
        )
      })}
    </div>
  )
})
